// React & Hooks
import { useEffect, useState, useMemo } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { AppProvider } from "@toolpad/core/react-router-dom";

// Material-UI (MUI)
import { createTheme } from "@mui/material";

// Icons (MUI)
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import StoreIcon from "@mui/icons-material/Store";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

// Utilities
import { jwtDecode } from "jwt-decode";
import * as services from "./services/saleng";
import {
  canAccessAdminPath,
  getAdminNavigation,
  getAdminRole,
  getAdminBranchId,
  isBranchScopedAdminRole,
  isSuperAdminOnlyRole,
  isSupportedAdminRole,
} from "./auth/adminAccess";

// Assets
import logo from "./images/scrapyard W.png";

const NAVIGATION = [
  {
    kind: "header",
    title: "รายการสินค้า",
  },
  {
    segment: "products",
    title: "รายการสินค้าทั้งหมด",
    icon: <LocalMallIcon />,
    pattern: "products{/:actions}*",
  },
  {
    segment: "categories",
    title: "หมวดหมู่สินค้า",
    icon: <LocalMallIcon />,
    pattern: "categories{/:actions}*",
  },
  {
    kind: "header",
    title: "การจัดการ",
  },
  {
    segment: "branches",
    title: "จัดการสาขา",
    icon: <StoreIcon />,
    pattern: "branches{/:actions}*",
  },
  {
    segment: "users",
    title: "จัดการพนักงาน",
    icon: <PeopleAltIcon />,
    pattern: "users{/:actions}*",
  },
  {
    segment: "groups",
    title: "จัดการกลุ่มลุกค้า",
    icon: <PeopleAltIcon />,
    pattern: "groups{/:actions}*",
  },
  {
    segment: "permissions",
    title: "สิทธิการเข้าถึง",
    icon: <VerifiedUserIcon />,
    pattern: "permissions{/:actions}*",
  },
  {
    segment: "super-admin",
    title: "จัดการร้าน",
    icon: <AdminPanelSettingsIcon />,
    pattern: "super-admin{/:actions}*",
  },
  // {
  //   kind: "header",
  //   title: "รายการอื่นๆ",
  // },
  // {
  //   segment: "reports",
  //   title: "ธุรกรรม",
  //   icon: <ReceiptLongIcon />,
  //   pattern: "reports{/:actions}*",
  // },
];

const theme = createTheme({
  typography: {
    fontFamily: '"IBM Plex Sans Thai", serif',
  },
  cssVariables: {
    colorSchemeSelector: "data-toolpad-color-scheme",
  },
  colorSchemes: { light: true, dark: true },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
           background:
            "linear-gradient(90deg, #185519 0%, #66BB6A 50%, #81C784 100%)",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#FCDE70",
            "&:hover": {
              backgroundColor: "#E8B86D",
            },
          },
        },
      },
    },
  },
});

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState({});
  const [role, setRole] = useState(() => getAdminRole());
  const [authReady, setAuthReady] = useState(false);
  const [branchAdminAllowed, setBranchAdminAllowed] = useState(true);

  const navigation = useMemo(() => getAdminNavigation(NAVIGATION, role), [role]);
  const canAccessCurrentPath =
    authReady &&
    branchAdminAllowed &&
    canAccessAdminPath(role, location.pathname);

  useEffect(() => {
    let isMounted = true;

    const logout = () => {
      localStorage.removeItem("accessTokenGreen");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("shopName");
      setSession(null);
      navigate("/auth/login");
    };

    const validateSession = async () => {
      const accessToken = localStorage.getItem("accessToken");
      const shopName = localStorage.getItem("shopName");

      if (!accessToken) {
        logout();
        return;
      }

      try {
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;

        if ((decoded?.exp ?? 0) < currentTime) {
          logout();
          return;
        }

        const currentRole = getAdminRole(decoded);
        if (!isSupportedAdminRole(currentRole)) {
          logout();
          return;
        }
        setRole(currentRole);

        if (isBranchScopedAdminRole(currentRole)) {
          const shopResponse = await services.getShop();
          const shop = shopResponse.data.data;
          const branchId = getAdminBranchId(decoded);
          const ownBranch = shop?.branches?.find(
            (branch) => Number(branch.id) === branchId,
          );
          const allowed =
            ownBranch?.priceAnnouncementType === "SELF_ANNOUNCE" &&
            ownBranch?.usePriceInsurance !== true;

          if (!allowed) {
            setBranchAdminAllowed(false);
            logout();
            return;
          }
        }

        const authResponse = await services.getAuthUser();
        const authUser = authResponse.data.data;
        const displayName =
          [authUser?.name, authUser?.lastname].filter(Boolean).join(" ") ||
          authUser?.username ||
          decoded?.username ||
          shopName;
        const displayRole =
          decoded?.app === "ADMIN"
            ? decoded?.adminRole || decoded?.role || currentRole
            : authUser?.role?.title || decoded?.role || currentRole;

        if (!isMounted) return;

        setSession({
          user: {
            name: displayName,
            email: `Role: ${displayRole}`,
          },
        });
        setBranchAdminAllowed(true);
        setAuthReady(true);
      } catch (error) {
        console.error("Error validating session:", error);
        logout();
      }
    };

    validateSession();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (!role || canAccessCurrentPath) return;
    navigate(
      isSuperAdminOnlyRole(role)
        ? "/super-admin"
        : isBranchScopedAdminRole(role)
          ? "/branches"
          : "/products",
      {
        replace: true,
      },
    );
  }, [canAccessCurrentPath, navigate, role]);

  const authentication = useMemo(
    () => ({
      signIn: () => {
        setSession({
          user: {
            name: "",
            email: "",
            image: <AccountCircleIcon />,
          },
        });
      },
      signOut: async () => {
        localStorage.removeItem("accessTokenGreen");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("shopName");
        setSession(null);
        navigate("/auth/login");
      },
    }),
    [navigate]
  );

  return (
    <AppProvider
      theme={theme}
      session={session}
      authentication={authentication}
      navigation={navigation}
      branding={{
          logo: <img src={logo} alt=" Logo" style={{ marginRight: 10 }} />,
        title: ""
      }}
    >
      {canAccessCurrentPath ? <Outlet /> : null}
    </AppProvider>
  );
}
